import type {FeedbackReaction} from '@Components/feedback-toast/feedback-toast.types';
import type {LayeringType} from '@Libraries/layering-type';

/** Query-string key for the one-shot post-layering feedback toast. Must match the `getGet('layeringFeedback')` read in Posterbuilder.php. */
const LAYERING_FEEDBACK_URL_PARAM = 'layeringFeedback';

export const recordLayeredPosterFeedbackReaction = async (posterHashedId: string, layeringType: LayeringType, reaction: FeedbackReaction): Promise<void> => {
  try {
    await window.PMW.writeLocal('imagetodesign/recordLayeredPosterFeedbackReaction', {posterHashedId, layeringType, reaction});
  } catch (e: unknown) {
    console.error(e);
  }
};

export const recordLayeredPosterFeedbackComment = async (posterHashedId: string, layeringType: LayeringType, comment: string): Promise<void> => {
  try {
    await window.PMW.writeLocal('imagetodesign/recordLayeredPosterFeedbackComment', {posterHashedId, layeringType, comment});
  } catch (e: unknown) {
    console.error(e);
  }
};

export const getUrlWithLayeringFeedbackParam = (url: string, layeringType: LayeringType): string => {
  const urlWithParam = new URL(url);
  urlWithParam.searchParams.set(LAYERING_FEEDBACK_URL_PARAM, layeringType);
  return urlWithParam.toString();
};

/**
 * Strips the one-shot `?layeringFeedback` param from the current URL so a reload does not re-show the toast
 * (the mobile fresh-layering flow appends it via {@link getUrlWithLayeringFeedbackParam}). No-op when absent.
 */
const clearLayeringFeedbackUrlParam = (): void => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(LAYERING_FEEDBACK_URL_PARAM)) {
    return;
  }
  url.searchParams.delete(LAYERING_FEEDBACK_URL_PARAM);
  window.history.replaceState(null, '', url.toString());
};

/**
 * Shows the post-layering feedback toast for the "Make text editable" (MTE) flow. Uses the shared
 * FeedbackToast's default (email AI-redesign) styling — standard card, `icon-sparkle`/`icon-message`
 * headers, inline send-icon comment box, and a success-banner thank-you — with layering-specific copy.
 * Feedback recording is best-effort (see recordLayeredPosterFeedbackReaction/Comment above), so the
 * toast's state machine never sticks on a network failure.
 */
export const showLayeredPosterFeedbackToast = (posterHashedId: string, layeringType: LayeringType): void => {
  clearLayeringFeedbackUrlParam();
  window.PMW.showFeedbackToast({
    onFeedbackReaction: async (reaction: FeedbackReaction): Promise<void> => recordLayeredPosterFeedbackReaction(posterHashedId, layeringType, reaction),
    onFeedbackTextInput: async (comment: string): Promise<void> => recordLayeredPosterFeedbackComment(posterHashedId, layeringType, comment),
    reactionPageHeaderTitle: window.i18next.t('pmwjs_ai_just_extracted_text_layers'),
    reactionPageHeaderIcon: 'icon-sparkle',
    feedbackInputHeaderTitle: window.i18next.t('pmwjs_help_us_improve'),
    feedbackInputHeaderIcon: 'icon-message',
    feedbackInputPlaceholder: window.i18next.t('pmwjs_explain_your_experience_ellipsis'),
    postSubmissionText: window.i18next.t('pmwjs_thanks_this_will_help_us_improve'),
  });
};
